import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { CARD_TYPES } from '../../../shared/constants';
import {
  IContentfulLink,
  IImage,
  IParentPage,
  Tag,
} from '../../../types/shared';

import Link from '../../atoms/link/Link';
import TagList from '../../atoms/tag-list/TagList';

import { DEQ_TYPES, trackClickEvent } from '../../../utils/deq';
import { getCtaLink, getInternalLink } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H3 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import FallbackIcon from '../../../images/pr-file-download.svg';

interface Props {
  image?: IImage;
  tags?: Tag[];
  title: string;
  subtitle?: string;
  primaryLink?: IContentfulLink;
  pageGenericLink?: IParentPage;
  children: ReactNode;
}

const GatedDownloadCard = ({
  image,
  tags,
  title,
  subtitle,
  primaryLink,
  pageGenericLink,
  children,
}: Props) => {
  const productPrimaryLink = primaryLink ? getCtaLink(primaryLink) : undefined;

  const pageLink = pageGenericLink
    ? `/${getInternalLink(
        pageGenericLink?.parentPage as IParentPage,
        pageGenericLink?.slug as string
      )}`
    : undefined;

  const hasIcon =
    image?.file?.url.includes('svg') || image?.file?.url.includes('png');

  return (
    <LinkContainer
      id={CARD_TYPES.gatedDownloadCard}
      href={pageLink || productPrimaryLink || '/404/'}
      customClick={() => trackClickEvent(productPrimaryLink)}
      trackingComponent="gated-content-card"
      trackingType={DEQ_TYPES.btnCard}
      trackingLabel={title || ''}
    >
      {image?.file && hasIcon ? (
        <ImageContainer>
          <img data-testid="image" alt="" src={image.file?.url} />
        </ImageContainer>
      ) : (
        <ImageContainer>
          <img data-testid="image" alt="" src={FallbackIcon} />
        </ImageContainer>
      )}
      {tags?.length ? (
        <StyledList data-testid="tags">
          <TagList tags={tags} />
        </StyledList>
      ) : undefined}
      {title && <Title data-testid="title">{title}</Title>}
      {subtitle && <Body data-testid="subtitle">{subtitle}</Body>}
      {children && (
        <StyledBody data-testid="description">{children}</StyledBody>
      )}
    </LinkContainer>
  );
};

export default GatedDownloadCard;

const LinkContainer = styled(Link)`
  background: ${Colors.white};
  border-radius: ${({ theme }) => theme.radiuses.M};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  height: auto;
  height: auto;
  padding: ${Spacings.S};
  width: 100%;

  ${mq.from.breakpoint.M`
    padding: ${Spacings.L};
    max-width: 976px;
  `};

  ${mq.from.breakpoint.XL`
    padding: ${Spacings.XL};
    max-width: 1272px;
  `};

  img {
    transition: transform 0.1s ease-in-out;
  }

  &:hover {
    img {
      transform: scale(1.1);
    }
  }
`;

const ImageContainer = styled.div`
  height: 72px;
  margin-right: ${Spacings.S};
  width: 72px;

  img {
    height: inherit;
    left: -8px;
    position: relative;
    top: -8px;
    width: inherit;
  }
`;

const StyledList = styled.ul`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${Spacings.XS};
  list-style: none;
  margin: 0 0 ${Spacings.XS} 0;
  padding: 0;
`;

const Title = styled(H3)`
  margin: 0 0 ${Spacings.XS};

  &:last-child {
    margin: 0;
  }

  &:first-child {
    margin: 0;
  }
`;

const StyledBody = styled(Body)`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;

  ul {
    margin-top: 0;
    padding-top: 0;
    padding-bottom: 0;

    li:last-of-type {
      margin-bottom: 0;
    }
  }

  p ~ ul {
    margin-top: ${Spacings.M};
    padding-top: 0;
  }

  p:last-of-type,
  ul:last-of-type {
    margin-bottom: 0;
  }

  &:last-child p:not(~ ul) {
    a {
      display: inline-flex;
      flex: 1;
      flex-direction: column;
      justify-content: flex-end;
    }
  }
`;
